/**
 * Portfolio-level insight math, kept pure so it can be unit-tested.
 * Used by: DashboardPage (slot analytics, change feed), InsightsPage
 * (round tendencies).
 */

// ── Draft-slot analytics ──────────────────────────────────────────

export interface SlotBand {
  label: string;
  count: number;
  avgClv: number;
}

const SLOT_BANDS: Array<{ label: string; min: number; max: number }> = [
  { label: "Slots 1–4", min: 1, max: 4 },
  { label: "Slots 5–8", min: 5, max: 8 },
  { label: "Slots 9–12", min: 9, max: 12 },
];

export function slotBandClv(teams: Array<{ slot: number; clv: number }>): SlotBand[] {
  return SLOT_BANDS.map(({ label, min, max }) => {
    const inBand = teams.filter(t => t.slot >= min && t.slot <= max && !isNaN(t.clv));
    const avg = inBand.length ? inBand.reduce((s, t) => s + t.clv, 0) / inBand.length : 0;
    return { label, count: inBand.length, avgClv: avg };
  });
}

// ── Round tendencies ──────────────────────────────────────────────

export interface PickForTendencies {
  position: string;
  pickNumber: number;
  marketAdp: number | null;
}

export interface PositionClv {
  position: string;
  /** Net CLV (yourPick − marketAdp, app convention): positive = discounts. */
  net: number;
  n: number;
}

/** Net CLV per position — which positions you gain or give back value on. */
export function clvByPosition(picks: PickForTendencies[]): PositionClv[] {
  const byPos = new Map<string, { net: number; n: number }>();
  for (const p of picks) {
    if (p.marketAdp === null || isNaN(p.pickNumber)) continue;
    const cur = byPos.get(p.position) ?? { net: 0, n: 0 };
    cur.net += p.pickNumber - p.marketAdp;
    cur.n++;
    byPos.set(p.position, cur);
  }
  const order = ["QB", "RB", "WR", "TE"];
  return order
    .filter(pos => byPos.has(pos))
    .map(pos => ({ position: pos, ...byPos.get(pos)! }));
}

export interface RoundBandClv {
  label: string;
  net: number;
  n: number;
}

const ROUND_BANDS: Array<{ label: string; min: number; max: number }> = [
  { label: "R1–4", min: 1, max: 4 },
  { label: "R5–8", min: 5, max: 8 },
  { label: "R9–12", min: 9, max: 12 },
  { label: "R13–16", min: 13, max: 16 },
  { label: "R17+", min: 17, max: Infinity },
];

/**
 * Net CLV grouped by the round you made the pick. Sign matches the app's
 * convention (`computePlayerCLV`): clv = yourPick − marketAdp, so positive =
 * you got the player later than the market drafts him (a discount).
 */
export function clvByRoundBand(
  picks: Array<{ pickNumber: number; marketAdp: number | null }>,
  draftSize = 12,
): RoundBandClv[] {
  return ROUND_BANDS.map(({ label, min, max }) => {
    let net = 0, n = 0;
    for (const p of picks) {
      if (p.marketAdp === null || isNaN(p.pickNumber)) continue;
      const round = Math.ceil(p.pickNumber / draftSize);
      if (round < min || round > max) continue;
      net += p.pickNumber - p.marketAdp;
      n++;
    }
    return { label, net, n };
  }).filter(b => b.n > 0);
}

// ── "Since your last visit" snapshot ──────────────────────────────

export interface VisitSnapshot {
  date: string; // YYYY-MM-DD
  portfolioClv: number;
  /** normalized player name → ADP at snapshot time */
  adps: Record<string, number>;
}

export interface FeedMover {
  name: string;
  /** ADP move since snapshot; positive = riser (ADP dropped = going earlier). */
  delta: number;
  exposure: number;
  impact: number;
}

export interface ChangeFeed {
  sinceDate: string;
  clvDelta: number;
  movers: FeedMover[];
}

/**
 * Diff the current state against a stored snapshot. Only players the user owns
 * (exposure > 0) appear; movers ranked by |delta × exposure| (portfolio impact).
 */
export function buildChangeFeed(
  snapshot: VisitSnapshot,
  currentClv: number,
  currentAdps: Map<string, number>,
  exposureByName: Map<string, number>,
  topN = 4,
): ChangeFeed {
  const movers: FeedMover[] = [];
  exposureByName.forEach((exposure, name) => {
    if (exposure <= 0) return;
    const then = snapshot.adps[name];
    const now = currentAdps.get(name);
    if (then === undefined || now === undefined) return;
    const delta = then - now; // ADP fell = rising in drafts = positive
    if (Math.abs(delta) < 0.05) return;
    movers.push({ name, delta, exposure, impact: (delta * exposure) / 100 });
  });
  movers.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
  return {
    sinceDate: snapshot.date,
    clvDelta: currentClv - snapshot.portfolioClv,
    movers: movers.slice(0, topN),
  };
}

// ── Entry duplication (portfolio_ev DUPLICATION read, client-side) ──
// Near-identical entries in the SAME tournament split their own equity in
// knockout groups: when two of your builds share most of a roster they boom
// and bust together and can knock each other out. Cross-tournament overlap
// is deliberately excluded — that's ordinary correlated exposure, already
// visible on the exposure views.

export interface DupEntryRef {
  draftId: string;
  date: string;
  draftSlot: number | string;
}

export interface DupPair {
  tournament: string;
  a: DupEntryRef;
  b: DupEntryRef;
  /** Players rostered by both entries. */
  shared: number;
  /** Roster size of the smaller entry (overlap denominator). */
  rosterSize: number;
  sharedNames: string[];
}

export interface DuplicationReport {
  /** Pairs sorted by shared-count desc, capped by `topN`. */
  pairs: DupPair[];
  /** Total same-tournament pairs compared. */
  totalPairs: number;
  /** Pairs sharing >= 7 players (p99 of real BBM self-overlap). */
  highPairs: number;
  /** Pairs sharing >= 10 players (0.1% of real pairs — near-clones). */
  clonePairs: number;
  maxShared: number;
}

export interface DupTeamLike {
  draftId: string;
  date: string;
  draftSlot: number | string;
  tournament: string;
  allPlayerNames: string[];
}

export function computeEntryDuplication(teams: DupTeamLike[], topN = 12): DuplicationReport {
  const byTournament = new Map<string, DupTeamLike[]>();
  teams.forEach(t => {
    const key = t.tournament || "(unknown)";
    const list = byTournament.get(key) ?? [];
    list.push(t);
    byTournament.set(key, list);
  });

  const pairs: DupPair[] = [];
  let totalPairs = 0;
  byTournament.forEach((entries, tournament) => {
    for (let i = 0; i < entries.length; i++) {
      const setA = new Set(entries[i].allPlayerNames);
      for (let j = i + 1; j < entries.length; j++) {
        totalPairs++;
        const sharedNames = entries[j].allPlayerNames.filter(n => setA.has(n));
        if (sharedNames.length === 0) continue;
        pairs.push({
          tournament,
          a: { draftId: entries[i].draftId, date: entries[i].date, draftSlot: entries[i].draftSlot },
          b: { draftId: entries[j].draftId, date: entries[j].date, draftSlot: entries[j].draftSlot },
          shared: sharedNames.length,
          rosterSize: Math.min(entries[i].allPlayerNames.length, entries[j].allPlayerNames.length),
          sharedNames,
        });
      }
    }
  });

  pairs.sort((x, y) => y.shared - x.shared);
  return {
    pairs: pairs.slice(0, topN),
    totalPairs,
    highPairs: pairs.filter(p => p.shared >= 7).length,
    clonePairs: pairs.filter(p => p.shared >= 10).length,
    maxShared: pairs.length ? pairs[0].shared : 0,
  };
}

// ── Duplication clusters ─────────────────────────────────────────────
// Pairs are the wrong display unit: four near-identical builds produce six
// pair rows that all say one thing — "this is a cluster". Group entries
// (same tournament) into connected components over the >=edgeThreshold
// overlap graph and surface each cluster's SHARED CORE (players on every
// entry in the cluster). Thresholds are anchored on measured BBM6
// self-overlap (8.5k multi-entry drafters): mean 2.1 shared, p95 = 5,
// p99 = 7, 10+ = 0.1% of pairs — so the default edge is 5 (top-5% most
// correlated), 7+ reads "high overlap", 10+ reads "near-clone".

export interface DupCluster {
  tournament: string;
  entries: DupEntryRef[];
  /** Players present in EVERY entry of the cluster. */
  coreNames: string[];
  /** Mean pairwise shared count across the cluster's edges. */
  meanShared: number;
  rosterSize: number;
}

export function computeDupClusters(teams: DupTeamLike[], edgeThreshold = 5): DupCluster[] {
  const byTournament = new Map<string, DupTeamLike[]>();
  teams.forEach(t => {
    const key = t.tournament || "(unknown)";
    const list = byTournament.get(key) ?? [];
    list.push(t);
    byTournament.set(key, list);
  });

  const clusters: DupCluster[] = [];
  byTournament.forEach((entries, tournament) => {
    const n = entries.length;
    const sets = entries.map(e => new Set(e.allPlayerNames));
    // all pairs meeting the bar, strongest first
    const pairList: Array<[number, number, number]> = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let c = 0;
        sets[j].forEach(pl => { if (sets[i].has(pl)) c++; });
        if (c >= edgeThreshold) pairList.push([c, i, j]);
      }
    }
    pairList.sort((a, b) => b[0] - a[0]);

    // Greedy core-preserving growth: seed with the strongest unused pair,
    // then add entries only while the GROUP intersection stays >= the bar.
    // Guarantees every shown cluster honors the slider value, and a strong
    // pair can never be hidden by a diluted chain (the old connected-
    // components approach dropped whole chains, hiding their strong pairs).
    const used = new Set<number>();
    pairList.forEach(([, i, j]) => {
      if (used.has(i) || used.has(j)) return;
      const members = [i, j];
      let core = [...sets[i]].filter(pl => sets[j].has(pl));
      let grew = true;
      while (grew) {
        grew = false;
        let best = -1;
        let bestCore: string[] = [];
        for (let k = 0; k < n; k++) {
          if (used.has(k) || members.includes(k)) continue;
          const cand = core.filter(pl => sets[k].has(pl));
          if (cand.length >= edgeThreshold && cand.length > bestCore.length) {
            best = k;
            bestCore = cand;
          }
        }
        if (best >= 0) {
          members.push(best);
          core = bestCore;
          grew = true;
        }
      }
      members.forEach(m => used.add(m));
      let sum = 0, edges = 0;
      for (let a = 0; a < members.length; a++) {
        for (let b = a + 1; b < members.length; b++) {
          let c = 0;
          sets[members[b]].forEach(pl => { if (sets[members[a]].has(pl)) c++; });
          sum += c; edges++;
        }
      }
      clusters.push({
        tournament,
        entries: members.map(m => ({
          draftId: entries[m].draftId, date: entries[m].date, draftSlot: entries[m].draftSlot,
        })),
        coreNames: core,
        meanShared: edges ? sum / edges : 0,
        rosterSize: Math.min(...members.map(m => entries[m].allPlayerNames.length)),
      });
    });
  });
  clusters.sort((a, b) => b.entries.length - a.entries.length || b.coreNames.length - a.coreNames.length);
  return clusters;
}

// ── Tournament uniqueness (portfolio-level, one number per tournament) ──
// "How duplicated am I in this tournament overall?" — mean pairwise
// shared players across your own entries, with the worst pair for
// context. Benchmark from measured BBM6 self-overlap: typical drafters
// average ~2 shared players (~11% of an 18-round roster).

export interface TournamentUniqueness {
  tournament: string;
  entryCount: number;
  /** Mean shared players across all own-entry pairs. */
  meanShared: number;
  /** Mean overlap as a share of the roster (0-1). */
  meanOverlapPct: number;
  maxShared: number;
  rosterSize: number;
}

export function computeTournamentUniqueness(teams: DupTeamLike[]): TournamentUniqueness[] {
  const byTournament = new Map<string, DupTeamLike[]>();
  teams.forEach(t => {
    const key = t.tournament || "(unknown)";
    const list = byTournament.get(key) ?? [];
    list.push(t);
    byTournament.set(key, list);
  });
  const out: TournamentUniqueness[] = [];
  byTournament.forEach((entries, tournament) => {
    if (entries.length < 2) return;
    const sets = entries.map(e => new Set(e.allPlayerNames));
    let sum = 0, edges = 0, maxShared = 0;
    for (let i = 0; i < sets.length; i++) {
      for (let j = i + 1; j < sets.length; j++) {
        let c = 0;
        sets[j].forEach(p => { if (sets[i].has(p)) c++; });
        sum += c; edges++;
        if (c > maxShared) maxShared = c;
      }
    }
    const rosterSize = Math.min(...entries.map(e => e.allPlayerNames.length)) || 18;
    const meanShared = edges ? sum / edges : 0;
    out.push({
      tournament, entryCount: entries.length, meanShared,
      meanOverlapPct: meanShared / rosterSize, maxShared, rosterSize,
    });
  });
  out.sort((a, b) => b.meanOverlapPct - a.meanOverlapPct);
  return out;
}
